import { ReactElement } from 'react';
import { HiOutlineXMark } from 'react-icons/hi2';
import Button from '../../components/Button';
import {
  FEEDBACK_STATUS,
  FEEDBACK_TYPE_LABELS,
  PRODUCT_AREA_LABELS,
} from '../../constants/feedback';
import { useFeedbackReport } from '../../hooks/useFeedback';
import FeedbackReportBody from '../../components/Feedback/FeedbackReportBody';
import { useFeedbackTriageEditor } from '../../components/Feedback/useFeedbackTriageEditor';
import { inputClass } from '../../components/formControlClasses';

interface FeedbackReportDetailProps {
  reportId: string;
  onClose: () => void;
}

/**
 * One report in full: what the user wrote, their screenshots, and the
 * auto-captured context. Triage actions are the ticket reference, a
 * resolution note and marking it processed.
 */
export default function FeedbackReportDetail({ reportId, onClose }: FeedbackReportDetailProps): ReactElement {
  const { data: report, isPending } = useFeedbackReport(reportId);
  const { ticketRef, setTicketRef, note, setNote, save, isReady, isSaving } = useFeedbackTriageEditor({
    reportId,
    report,
    closedToast: 'Marked processed',
  });

  return (
    <div className="cr-fixed cr-inset-0 cr-z-[19500] cr-flex cr-items-center cr-justify-center cr-bg-black/40">
      <div
        className="cr-flex cr-max-h-[85vh] cr-w-[min(760px,calc(100vw-2rem))] cr-flex-col cr-gap-3
          cr-overflow-y-auto cr-rounded-lg cr-bg-white cr-p-5 cr-shadow-2xl"
      >
        <div className="cr-flex cr-items-start cr-justify-between">
          <div>
            <h3 className="cr-m-0 cr-text-lg cr-font-semibold cr-text-blue-900">
              {isPending ? 'Loading…' : report?.title}
            </h3>
            {report && (
              <p className="cr-m-0 cr-text-sm cr-text-gray-600">
                {FEEDBACK_TYPE_LABELS[report.feedback_type]} · {PRODUCT_AREA_LABELS[report.product_area]} ·{' '}
                {new Date(report.created_at).toLocaleString()}
              </p>
            )}
          </div>
          <button
            type="button"
            aria-label="Close report"
            className="cr-cursor-pointer cr-border-0 cr-bg-transparent cr-text-blue-900"
            onClick={onClose}
          >
            <HiOutlineXMark size={20} />
          </button>
        </div>

        {report && <FeedbackReportBody report={report} />}

        <div className="cr-flex cr-flex-col cr-gap-2">
          <label className="cr-text-sm cr-font-semibold cr-text-blue-900">
            Stream Punk ticket
            <input
              className={inputClass}
              value={ticketRef}
              placeholder="Ticket UUID or reference"
              onChange={(event) => setTicketRef(event.target.value)}
            />
          </label>
          <label className="cr-text-sm cr-font-semibold cr-text-blue-900">
            Resolution note
            <textarea
              className={inputClass}
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
        </div>

        <div className="cr-flex cr-justify-end cr-gap-2">
          <Button variant="ghost" size="compact" normalCase onClick={onClose}>Close</Button>
          <Button
            variant="secondary"
            size="compact"
            normalCase
            disabled={isSaving || !isReady}
            onClick={() => save()}
          >
            Save
          </Button>
          <Button
            variant="outline"
            size="compact"
            normalCase
            disabled={isSaving || !isReady || report?.status === FEEDBACK_STATUS.PROCESSED}
            onClick={() => save(FEEDBACK_STATUS.PROCESSED)}
          >
            Mark processed
          </Button>
        </div>
      </div>
    </div>
  );
}
